<script setup lang="ts">
import ConfirmActionDialog from "@/components/dialogs/ConfirmActionDialog.vue";
import AppDialog from "@/components/shared/AppDialog.vue";
import StockPriceFormLayout from "@/components/warehouse/StockPriceFormLayout.vue";
import StockPriceGetItemsDialog from "@/components/warehouse/StockPriceGetItemsDialog.vue";
import StockPriceItemsTable from "@/components/warehouse/StockPriceItemsTable.vue";
import { GetStockPriceDetail, UpdateStockPrice } from "@/api/stockPriceApi";
import { useStockPriceActions } from "@/composables/useStockPriceActions";
import { useStockPriceForm } from "@/composables/useStockPriceForm";
import { canDoctype } from "@/helper/permissionHelper";
import { presentStockPriceError } from "@/services/stockPriceErrors";
import useToastify from "@/stores/toastify.store";
import type { StockPriceCandidateItem, StockPriceDetail } from "@/type/stockPrice";

/** ⚠️ Inline literal — see the note in `index.vue`. */
definePage({ meta: { permissionKey: "page.warehouse.stock_prices" } });

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const toast = useToastify();

/**
 * ⚠️ Cast rather than read off the generated route type. `typed-router.d.ts` is
 * regenerated by the dev server and the build, and a freshly added route's params
 * are not narrowed until it has been. Reading `route.params.id` directly here
 * fails to typecheck on a clean checkout.
 */
const stockPriceId = computed(() => String((route.params as Record<string, unknown>)?.id ?? ""));

const stockPrice = ref<StockPriceDetail | null>(null);
const loading = ref(false);
const saving = ref(false);
/** A failed READ. Distinct from "this document has no rows", which is a real state. */
const loadFailed = ref(false);
const getItemsVisible = ref(false);
const acknowledgeFirstPrices = ref(false);

const inlineError = ref<{ title: string | null; message: string | null; guidance: string | null } | null>(null);

const isEditing = ref(route.query.mode === "edit");

const formState = useStockPriceForm({ stockPrice, isEditing });

const canWrite = computed(() => canDoctype("Stock Price", "write"));

async function fetchDetail() {
  loadFailed.value = false;

  const detail = await GetStockPriceDetail(loading, stockPriceId.value);

  if (!detail) {
    loadFailed.value = true;

    return;
  }

  stockPrice.value = detail;

  // A submitted or cancelled document can never go back into edit mode, whatever
  // the query string says.
  if (detail.status !== "Draft")
    isEditing.value = false;
}

onMounted(fetchDetail);

const actions = useStockPriceActions({
  onChanged: async (updated) => {
    if (updated)
      stockPrice.value = updated;
    else
      await fetchDetail();

    acknowledgeFirstPrices.value = false;
  },
  onDeleted: async () => {
    await router.push("/warehouse/stock-prices");
  },
  onError: (presentation) => {
    inlineError.value = {
      title: presentation.alertTitle,
      message: presentation.alertMessage,
      guidance: presentation.guidance,
    };
  },
});

const existingItemCodes = computed(() =>
  formState.rows.value.map(row => String(row.itemCode ?? "")).filter(Boolean));

const canEdit = computed(() => actions.canEdit(stockPrice.value));
const canSave = computed(() =>
  isEditing.value && canWrite.value && !saving.value && formState.rows.value.length > 0);

const firstSellingCount = computed(() => formState.firstSellingPriceRows.value.length);
const multiRowCount = computed(() => formState.multiRowRows.value.length);

/**
 * ⚠️ Submit is blocked on THREE separate things, and the disabled tooltip has to
 * say which, or the button just looks broken:
 *  - the permission and docstatus gate (`actions.canSubmit`)
 *  - a document whose every row is Skipped or Unchanged, which the backend
 *    refuses because a submitted document that did nothing is a lie in the audit
 *    trail
 *  - unsaved edits, because submit applies the STORED document, so the Action
 *    column on screen would be describing something else
 */
const submitBlockedReason = computed(() => {
  if (!formState.hasApplicableRows.value)
    return t("alkokh.stockPrice.warnings.nothingToApplyBody");
  if (formState.isDirty.value)
    return t("alkokh.stockPrice.warnings.unsavedBody");

  return "";
});

const canSubmitNow = computed(() =>
  actions.canSubmit(stockPrice.value) && !submitBlockedReason.value);

const summaryRows = computed(() => {
  const counts = formState.actionCounts.value;

  return [
    { key: "selling", label: t("alkokh.stockPrice.summary.selling"), counts: counts.selling },
    { key: "buying", label: t("alkokh.stockPrice.summary.buying"), counts: counts.buying },
  ];
});

function onAddItems(items: StockPriceCandidateItem[]) {
  const { added, skipped } = formState.appendRows(items);

  if (added)
    toast.success({ text: t("alkokh.stockPrice.messages.itemsAdded", { count: added }) });
  else if (skipped)
    toast.warning({ text: t("alkokh.stockPrice.messages.itemsAllPresent") });
}

async function save() {
  if (!canSave.value)
    return;

  inlineError.value = null;

  try {
    const updated = await UpdateStockPrice(saving, stockPriceId.value, formState.buildPayload());
    if (!updated)
      return;

    stockPrice.value = updated;
    isEditing.value = false;
    toast.success({ text: t("alkokh.stockPrice.messages.updated") });
  } catch (error: any) {
    const presentation = presentStockPriceError(error, t);

    toast.error({ text: presentation.toastMessage });
    inlineError.value = {
      title: presentation.alertTitle,
      message: presentation.alertMessage,
      guidance: presentation.guidance,
    };
  }
}

function askSubmit() {
  acknowledgeFirstPrices.value = false;
  inlineError.value = null;
  actions.askSubmit(stockPrice.value);
}

function askCancel() {
  inlineError.value = null;
  actions.askCancel(stockPrice.value);
}

/**
 * ⚠️ The acknowledgement is REQUIRED only when this document creates a first
 * selling price for at least one item. Creating one moves that item's price
 * source away from the item-level fallback the rest of the app reads — a change
 * of where the price comes from, not just what it is, which nobody would infer
 * from a chip in a grid.
 */
const submitConfirmDisabled = computed(() =>
  firstSellingCount.value > 0 && !acknowledgeFirstPrices.value);
</script>

<template>
  <div>
    <VAlert
      v-if="loadFailed"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      <template #title>
        {{ t("alkokh.stockPrice.notFound") }}
      </template>

      <VBtn
        class="mt-2"
        size="small"
        variant="tonal"
        @click="fetchDetail"
      >
        {{ t("alkokh.stockPrice.retry") }}
      </VBtn>
    </VAlert>

    <StockPriceFormLayout
      v-else
      :form-state="formState"
      mode="detail"
      :stock-price="stockPrice"
      :loading="loading"
    >
      <template #actions>
        <VBtn
          variant="text"
          to="/warehouse/stock-prices"
        >
          {{ t("alkokh.stockPrice.backToList") }}
        </VBtn>

        <VBtn
          v-if="canEdit && !isEditing"
          variant="tonal"
          prepend-icon="tabler-edit"
          @click="isEditing = true"
        >
          {{ t("alkokh.stockPrice.editAction") }}
        </VBtn>

        <VBtn
          v-if="isEditing"
          color="primary"
          variant="flat"
          prepend-icon="tabler-device-floppy"
          :disabled="!canSave"
          :loading="saving"
          @click="save"
        >
          {{ t("alkokh.global.save") }}
        </VBtn>

        <VBtn
          v-if="actions.canSubmit(stockPrice)"
          color="success"
          variant="flat"
          prepend-icon="tabler-check"
          :disabled="!canSubmitNow"
          @click="askSubmit"
        >
          {{ t("alkokh.stockPrice.submitDialog.confirm") }}

          <VTooltip
            v-if="submitBlockedReason"
            activator="parent"
            location="bottom"
          >
            {{ submitBlockedReason }}
          </VTooltip>
        </VBtn>

        <VBtn
          v-if="actions.canCancel(stockPrice)"
          color="error"
          variant="tonal"
          prepend-icon="tabler-arrow-back-up"
          @click="askCancel"
        >
          {{ t("alkokh.stockPrice.cancelDialog.confirm") }}
        </VBtn>

        <VBtn
          v-if="actions.canDelete(stockPrice)"
          color="error"
          variant="text"
          icon="tabler-trash"
          :aria-label="t('alkokh.stockPrice.deleteDialog.confirm')"
          @click="actions.askDelete(stockPrice)"
        />
      </template>

      <!--
        ⚠️ ONE primary button plus ONE overflow menu, and it must stay that way.
        `MobilePageTitle` gives its action block `flex: 0 0 auto` and lets the
        title shrink, so forwarding the five desktop buttons squeezed
        "STK-PRC-2026-00001" down to a single character per line and pushed Apply
        off the screen. `AppDocumentHeader` says as much above its template; this
        slot is the answer to it. Anything new belongs in the menu, not beside it.
      -->
      <template #mobileActions>
        <div class="d-flex align-center gap-1">
          <VBtn
            v-if="isEditing"
            color="primary"
            variant="flat"
            size="small"
            :disabled="!canSave"
            :loading="saving"
            @click="save"
          >
            {{ t("alkokh.global.save") }}
          </VBtn>

          <VBtn
            v-else-if="actions.canSubmit(stockPrice)"
            color="success"
            variant="flat"
            size="small"
            :disabled="!canSubmitNow"
            @click="askSubmit"
          >
            {{ t("alkokh.stockPrice.submitDialog.confirm") }}
          </VBtn>

          <VMenu>
            <template #activator="{ props: menuProps }">
              <VBtn
                v-bind="menuProps"
                icon="tabler-dots-vertical"
                variant="text"
                size="small"
                :aria-label="t('alkokh.global.actions')"
              />
            </template>

            <VList density="compact">
              <VListItem
                prepend-icon="tabler-arrow-left"
                :title="t('alkokh.stockPrice.backToList')"
                to="/warehouse/stock-prices"
              />
              <VListItem
                v-if="canEdit && !isEditing"
                prepend-icon="tabler-edit"
                :title="t('alkokh.stockPrice.editAction')"
                @click="isEditing = true"
              />
              <VListItem
                v-if="actions.canCancel(stockPrice)"
                prepend-icon="tabler-arrow-back-up"
                :title="t('alkokh.stockPrice.cancelDialog.confirm')"
                @click="askCancel"
              />
              <VListItem
                v-if="actions.canDelete(stockPrice)"
                prepend-icon="tabler-trash"
                :title="t('alkokh.stockPrice.deleteDialog.confirm')"
                @click="actions.askDelete(stockPrice)"
              />
            </VList>
          </VMenu>
        </div>
      </template>

      <template #alert>
        <VAlert
          v-if="inlineError"
          type="error"
          variant="tonal"
          class="mb-4"
          closable
          @click:close="inlineError = null"
        >
          <template
            v-if="inlineError.title"
            #title
          >
            {{ inlineError.title }}
          </template>

          <div v-if="inlineError.message">
            {{ inlineError.message }}
          </div>
          <div
            v-if="inlineError.guidance"
            class="mt-2 font-weight-medium"
          >
            {{ inlineError.guidance }}
          </div>
        </VAlert>

        <VAlert
          v-if="formState.isCancelled.value"
          type="info"
          variant="tonal"
          class="mb-4"
        >
          <template #title>
            {{ t("alkokh.stockPrice.effect.cancelledTitle") }}
          </template>

          {{ t("alkokh.stockPrice.effect.cancelledDesc") }}

          <div class="mt-2">
            <VBtn
              size="small"
              variant="tonal"
              prepend-icon="tabler-plus"
              to="/warehouse/stock-prices/create"
            >
              {{ t("alkokh.stockPrice.effect.correctForward") }}
            </VBtn>
          </div>
        </VAlert>
      </template>

      <template #items>
        <StockPriceItemsTable
          :form-state="formState"
          :historical="formState.isHistorical.value"
          :readonly="!isEditing"
          :loading="loading"
          @add-items="getItemsVisible = true"
        />
      </template>

      <template #aside>
        <!-- What applying this will do, drawn from the SAVED actions. -->
        <VCard
          v-if="formState.isDraft.value"
          variant="outlined"
        >
          <VCardItem>
            <VCardTitle class="text-subtitle-1">
              {{ t("alkokh.stockPrice.summary.title") }}
            </VCardTitle>
          </VCardItem>

          <VCardText>
            <div
              v-for="row in summaryRows"
              :key="row.key"
              class="mb-3"
            >
              <div class="text-body-2 font-weight-medium mb-1">
                {{ row.label }}
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  size="small"
                  color="success"
                  variant="tonal"
                >
                  {{ t("alkokh.stockPrice.summary.create") }}: {{ row.counts.create }}
                </VChip>
                <VChip
                  size="small"
                  color="primary"
                  variant="tonal"
                >
                  {{ t("alkokh.stockPrice.summary.update") }}: {{ row.counts.update }}
                </VChip>
                <VChip
                  size="small"
                  color="secondary"
                  variant="tonal"
                >
                  {{ t("alkokh.stockPrice.summary.unchanged") }}: {{ row.counts.unchanged }}
                </VChip>
                <VChip
                  size="small"
                  variant="tonal"
                >
                  {{ t("alkokh.stockPrice.summary.skipped") }}: {{ row.counts.skipped }}
                </VChip>
              </div>
            </div>

            <VDivider class="my-3" />

            <div class="text-body-2">
              {{ t("alkokh.stockPrice.summary.applicable", { count: formState.applicableRows.value.length }) }}
            </div>
          </VCardText>
        </VCard>

        <VAlert
          v-if="formState.isDraft.value && formState.isDirty.value"
          type="warning"
          variant="tonal"
          density="comfortable"
        >
          <template #title>
            {{ t("alkokh.stockPrice.warnings.unsavedTitle") }}
          </template>
          {{ t("alkokh.stockPrice.warnings.unsavedBody") }}
        </VAlert>

        <VAlert
          v-if="formState.isDraft.value && !formState.hasApplicableRows.value && !formState.isDirty.value"
          type="info"
          variant="tonal"
          density="comfortable"
        >
          <template #title>
            {{ t("alkokh.stockPrice.warnings.nothingToApplyTitle") }}
          </template>
          {{ t("alkokh.stockPrice.warnings.nothingToApplyBody") }}
        </VAlert>

        <VAlert
          v-if="multiRowCount"
          type="warning"
          variant="tonal"
          density="comfortable"
        >
          <template #title>
            {{ t("alkokh.stockPrice.warnings.multiRowTitle") }}
          </template>

          {{ t("alkokh.stockPrice.warnings.multiRowBody", { count: multiRowCount }) }}

          <ul class="mt-2 ps-4">
            <li
              v-for="row in formState.multiRowRows.value"
              :key="row.id"
            >
              {{ row.itemName || row.itemCode }}
            </li>
          </ul>
        </VAlert>

        <VAlert
          v-if="firstSellingCount"
          type="warning"
          variant="tonal"
          density="comfortable"
        >
          <template #title>
            {{ t("alkokh.stockPrice.warnings.firstSellingTitle") }}
          </template>
          {{ t("alkokh.stockPrice.warnings.firstSellingBody", { count: firstSellingCount }) }}
        </VAlert>

        <VAlert
          v-if="formState.isSubmitted.value"
          type="success"
          variant="tonal"
          density="comfortable"
        >
          <template #title>
            {{ t("alkokh.stockPrice.effect.submittedTitle") }}
          </template>
          {{ t("alkokh.stockPrice.effect.submittedDesc") }}
        </VAlert>
      </template>

      <StockPriceGetItemsDialog
        v-model="getItemsVisible"
        :existing-item-codes="existingItemCodes"
        @add="onAddItems"
      />
    </StockPriceFormLayout>

    <!--
      ⚠️ An AppDialog rather than the shared ConfirmActionDialog, because this
      confirmation is not a plain yes/no. When the document creates an item's
      FIRST selling price it must carry a required acknowledgement, and
      ConfirmActionDialog has no slot to put one in.
    -->
    <AppDialog
      v-model="actions.submitDialogVisible.value"
      variant="confirm"
      :title="t('alkokh.stockPrice.submitDialog.title')"
    >
      <div class="mb-3">
        {{ t("alkokh.stockPrice.submitDialog.message") }}
      </div>

      <div class="mb-3 text-body-2">
        {{ t("alkokh.stockPrice.summary.applicable", { count: formState.applicableRows.value.length }) }}
      </div>

      <VAlert
        v-if="firstSellingCount"
        type="warning"
        variant="tonal"
        density="compact"
        class="mb-3"
      >
        {{ t("alkokh.stockPrice.warnings.firstSellingBody", { count: firstSellingCount }) }}
      </VAlert>

      <VCheckbox
        v-if="firstSellingCount"
        v-model="acknowledgeFirstPrices"
        color="warning"
        density="compact"
        hide-details
        :label="t('alkokh.stockPrice.warnings.firstSellingAck', { count: firstSellingCount })"
      />

      <template #actions>
        <VSpacer />
        <VBtn
          variant="text"
          @click="actions.submitDialogVisible.value = false"
        >
          {{ t("alkokh.global.cancel") }}
        </VBtn>
        <VBtn
          color="success"
          variant="flat"
          :disabled="submitConfirmDisabled"
          :loading="actions.submitting.value"
          @click="actions.submitStockPrice()"
        >
          {{ t("alkokh.stockPrice.submitDialog.confirm") }}
        </VBtn>
      </template>
    </AppDialog>

    <ConfirmActionDialog
      v-model:is-dialog-visible="actions.cancelDialogVisible.value"
      :title="t('alkokh.stockPrice.cancelDialog.title')"
      :message="`${t('alkokh.stockPrice.cancelDialog.message')} ${t('alkokh.stockPrice.cancelDialog.warning')}`"
      :confirm-btn="t('alkokh.stockPrice.cancelDialog.confirm')"
      confirm-color="error"
      icon="tabler-arrow-back-up"
      :loading="actions.actionLoading.value"
      @confirm="actions.cancelStockPrice()"
    />

    <ConfirmActionDialog
      v-model:is-dialog-visible="actions.deleteDialogVisible.value"
      :title="t('alkokh.stockPrice.deleteDialog.title')"
      :message="t('alkokh.stockPrice.deleteDialog.message')"
      :confirm-btn="t('alkokh.stockPrice.deleteDialog.confirm')"
      confirm-color="error"
      icon="tabler-trash"
      :loading="actions.actionLoading.value"
      @confirm="actions.deleteDraft()"
    />
  </div>
</template>
